import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
} from "@mui/material";
import {
  FileDownload,
  FilterList,
  ImportExport,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreHoriz,
  Search,
  ViewWeekOutlined,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import NoDataOverlay from "../NoDataOverlay";
import SortModelButton from "../SortModelButton";
import AppMenuBar from "../AppMenuBar";
import { TablePaginationActions } from "../CustomPagination";

/**
 * @author Sarthak Sharma.
 * @since 27-12-2023.
 * @description Make Account Card component.
 */

const SummaryDataGrid = (props) => {
  const {
    title,
    subTitle,
    columns,
    onPressDelete,
    isDeleteAllBtn = true,
    isPagination = true,
    onSearchTermChange,
    searchTerm,
    onClick,
  } = props;

  const [showModal, setShowModal] = useState(true);
  const [sortModel, setSortModel] = useState([]);

  const onChangeSortModel = (model, details) => {
    setSortModel(model);
  };

  const handleDownload = () => {
    onClick();
  };

  const optionData = [{ title: "Delete", onPress: onPressDelete }];

  const CustomToolbar = useCallback(
    ({ searchTerm, onChangeSearchTerm, optionData }) => {
      const onChangeSearch = (event) => {
        onChangeSearchTerm(event.target.value);
      };
      return (
        <div className="flex flex-wrap justify-between px-3">
          <div className="">
            <div className="text-neutral-800 text-base font-['Poppins-Medium'] leading-[30px]">
              {title}
            </div>

            <div className="text-neutral-800 text-[14px] font-['Poppins-Regular'] leading-[30px]">
              {subTitle}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 ml-auto mr-1">
            {isPagination && (
              <div className="flex flex-wrap border-[1px] border-[#AEB4B9] rounded-[4px]">
                <Button className="flex items-center gap-1">
                  <GridToolbarFilterButton className="opacity-0 absolute" />
                  <FilterList className="text-[22px] text-neutral-800" />
                  <div className="text-neutral-800 text-sm font-['Poppins-Regular'] leading-tight">
                    Filter
                  </div>
                </Button>
                <Divider
                  orientation="vertical"
                  flexItem
                  className="bg-[#AEB4B9]"
                />
                <Button className="flex items-center gap-1">
                  <GridToolbarColumnsButton className="opacity-0 absolute" />
                  <ViewWeekOutlined className="text-[22px] text-neutral-800" />
                  <div className="text-neutral-800 text-sm font-['Poppins-Regular'] leading-tight">
                    Column
                  </div>
                </Button>
                <Divider
                  orientation="vertical"
                  flexItem
                  className="bg-[#AEB4B9]"
                />
                <SortModelButton
                  columns={columns}
                  sortModel={sortModel}
                  onChangeSortModel={onChangeSortModel}
                >
                  <div className="flex items-center gap-1">
                    <ImportExport className="text-[22px] text-neutral-800" />
                    <div className="text-neutral-800 text-sm font-['Poppins-Regular'] leading-tight">
                      Sort
                    </div>
                  </div>
                </SortModelButton>
              </div>
            )}

            {isDeleteAllBtn && (
              <div className="flex border-[1px] border-[#AEB4B9] rounded-[4px]">
                <AppMenuBar options={optionData}>
                  <MoreHoriz className="text-[22px] text-neutral-800" />
                </AppMenuBar>
              </div>
            )}

            <div className="flex border-[1px] border-[#AEB4B9] rounded-[4px]">
              <IconButton size="small" onClick={handleDownload}>
                <FileDownload className="text-[22px] text-neutral-800" />
              </IconButton>
            </div>
          </div>

          <div>
            <IconButton
              onClick={setShowModal?.bind(null, !showModal)}
              size="small"
            >
              {showModal ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </div>
          {!!onChangeSearchTerm && (
            <div className="w-full mt-2">
              <FormControl className="w-full rounded-[4px] border-[1px] border-solid border-[#e1e3e5]">
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  value={searchTerm}
                  // defaultValue={searchTerm}
                  onChange={onChangeSearch}
                  type="search"
                  size="small"
                  placeholder="Search"
                  className="text-[#5c5f62] text-[14px] rounded-[4px]"
                />
              </FormControl>
            </div>
          )}
        </div>
      );
    },
    [showModal]
  );

  return (
    <Box className="flex flex-col gap-1 py-5 px-2 w-full">
      <Box
        className="data-grid transition-[height] duration-200"
        sx={{
          maxHeight: showModal ? 800 : 60,
          width: "100%",
          overflowY: "hidden",
        }}
      >
        <DataGrid
          {...props}
          rows={props.rows}
          autoHeight={props.rows?.length <= 8}
          slots={{
            toolbar: CustomToolbar,
            noResultsOverlay: NoDataOverlay,
            loadingOverlay: LinearProgress,
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            noRowsOverlay: {
              title: "No Data found",
            },
            noResultsOverlay: {
              title: "No Result found",
            },
            pagination: {
              ActionsComponent: TablePaginationActions,
            },
            toolbar: {
              searchTerm: searchTerm,
              onChangeSearchTerm: onSearchTermChange,
              optionData: optionData,
            },
          }}
          sortModel={sortModel}
          onSortModelChange={onChangeSortModel}
          filterMode="server"
          paginationMode="server"
          pageSizeOptions={[5, 10, 20, 50, 75, 100]}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};

export default SummaryDataGrid;
